import styled from 'styled-components';

export const PaymentPageStyled = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

export const PaymentSectionStyled = styled.section`
    padding: 20px 16px;
    background: var(--color-surface);
    border-radius: 8px;

    .section-header {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px 24px;
        margin-bottom: 20px;
    }

    .section-heading {
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-width: 0;
    }

    .section-step {
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--color-accent);
    }

    h4 {
        margin: 0;
        font-size: 20px;
        font-weight: 600;
        color: var(--color-white-95);
    }

    .section-desc {
        margin: 0;
        font-size: 14px;
        color: var(--text-color-secondary);
    }
`;

export const PaymentBalanceStyled = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    padding: 12px 16px;
    background: var(--color-surface-secondary);
    border-radius: 8px;
    min-width: 160px;

    .balance-label {
        font-size: 12px;
        color: var(--text-color-secondary);
    }

    .balance-value {
        font-size: 24px;
        font-weight: 600;
        line-height: 1.2;
        color: var(--color-white-95);
    }
`;

export const PaymentDestinationSummaryStyled = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 8px 12px;
    margin-bottom: 16px;
    padding: 12px 16px;
    background: var(--color-surface-secondary);
    border-radius: 8px;

    .dest-label {
        font-size: 12px;
        color: var(--text-color-secondary);
        white-space: nowrap;
    }

    .dest-method {
        font-size: 14px;
        font-weight: 600;
        color: var(--color-white-95);
    }

    .dest-info {
        flex: 1 1 100%;
        font-size: 13px;
        color: var(--color-white-60);
        word-break: break-word;
    }
`;

export const PaymentFormGridStyled = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;

    @media (min-width: 992px) {
        grid-template-columns: minmax(220px, 1fr) minmax(0, 2fr);
        align-items: start;
    }

    .form-control,
    .form-select {
        border: 1px solid var(--color-main);
    }

    textarea.form-control {
        min-height: 96px;
        resize: vertical;
    }
`;

export const PaymentAmountRowStyled = styled.div`
    max-width: 420px;

    .amount-group {
        margin-bottom: 0;
    }

    .amount-input-row {
        display: flex;
        flex-wrap: wrap;
        align-items: stretch;
        gap: 12px;
    }

    .amount-input-row .form-control {
        flex: 1 1 160px;
        border: 1px solid var(--color-main);
    }

    .btn {
        flex: 0 0 auto;
        align-self: stretch;
        white-space: nowrap;
        min-height: 56px;
    }
`;

export const PaymentNoticeStyled = styled.div`
    margin: 12px 0 0;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 14px;
    line-height: 1.4;

    &.info {
        background: var(--background-neutral-20);
        color: var(--color-secondary-highlight);
    }

    &.warning {
        background: rgba(163, 134, 0, 0.22);
        color: var(--color-yellow-light);
    }

    &.error {
        background: rgba(208, 82, 87, 0.2);
        color: var(--color-negative);
    }
`;

export const PaymentHintStyled = styled.p`
    margin: 6px 0 0;
    font-size: 13px;
    color: var(--text-color-secondary);
`;

export const PaymentActionsStyled = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    margin-top: 16px;
`;

export const PaymentHistoryTableStyled = styled.table`
    width: 100%;
    border-collapse: collapse;
    background: var(--color-surface-secondary);
    border-radius: 8px;
    overflow: hidden;

    th,
    td {
        padding: 14px 16px;
        text-align: left;
        vertical-align: middle;
    }

    th {
        font-size: 12px;
        font-weight: 400;
        color: var(--color-white-60);
        border-bottom: 1px solid var(--color-surface);
    }

    td {
        font-size: 14px;
        font-weight: 400;
        color: var(--color-white-95);
        border-top: 1px solid var(--color-surface);
    }

    tbody tr:first-child td {
        border-top: none;
    }

    .col-id {
        width: 72px;
        color: var(--color-white-60);
    }

    .col-amount {
        font-weight: 600;
        white-space: nowrap;
    }

    .col-date {
        color: var(--color-white-60);
        white-space: nowrap;
    }

    .empty-cell {
        padding: 32px 16px;
        text-align: center;
        color: var(--text-color-secondary);
    }

    @media (max-width: 767px) {
        display: block;

        thead {
            display: none;
        }

        tbody,
        tr,
        td {
            display: block;
            width: 100%;
        }

        tr {
            padding: 12px 0;
            border-top: 1px solid var(--color-surface);
        }

        tr:first-child {
            border-top: none;
        }

        td {
            display: flex;
            justify-content: space-between;
            gap: 12px;
            padding: 6px 16px;
            border-top: none;
        }

        td::before {
            content: attr(data-label);
            color: var(--color-white-60);
            font-weight: 400;
        }

        .empty-cell {
            display: block;
            text-align: center;
        }

        .empty-cell::before {
            content: none;
        }
    }
`;

export const PaymentStatusStyled = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 500;
    color: var(--color-accent);
    text-transform: capitalize;

    .indicator {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: var(--color-accent);
        flex-shrink: 0;
    }

    &.positive {
        color: var(--color-positive);

        .indicator {
            background: var(--color-positive);
        }
    }

    &.negative {
        color: var(--color-negative);

        .indicator {
            background: var(--color-negative);
        }
    }

    &.muted {
        color: var(--color-white-60);

        .indicator {
            background: var(--color-white-60);
        }
    }
`;
